import React, { FC, useRef } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import { ThemeConsumer } from "@rneui/themed";
import { RichEditor } from "react-native-pell-rich-editor";

import { ZenEditor, ZenText, ZenToolbar, actions } from "zen-core-elements";

type CommonTextEditorProps = {
    label?: string
    value: string
    onChange: (html: string) => void
    placeholder?: string
    disabled?: boolean
    /** Minimum height of the editing area */
    height?: number
    /** Toolbar actions, defaults to basic text formatting and alignment */
    toolbarActions?: string[]
    containerStyle?: StyleProp<ViewStyle>
}

const defaultActions = [
    actions.setBold,
    actions.setItalic,
    actions.setUnderline,
    actions.alignLeft,
    actions.alignCenter,
    actions.alignRight,
    actions.undo,
    actions.redo
]

//Method to convert a plain text value (saved before the editor was used) into HTML so line breaks are kept
export const toEditorHtml = (value?: string | null) => {
    if (!value) return ""
    if (/<[a-z][\s\S]*>/i.test(value)) return value
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>")
}

//Method to check whether the editor HTML has any visible text
export const isEditorHtmlEmpty = (html?: string | null) => (
    !html || !html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim()
)

const CommonTextEditor: FC<CommonTextEditorProps> = ({
    label,
    value,
    onChange,
    placeholder,
    disabled = false,
    height = 110,
    toolbarActions = defaultActions,
    containerStyle
}) => {
    const editorRef = useRef<RichEditor>(null)

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <View style={containerStyle}>
                    {label ? <ZenText content={label} style={[theme?.addEditCustomerStyle?.labelStyle, { marginBottom: 5 }]} /> : null}
                    <View style={{ borderWidth: 1, borderColor: theme?.borderColor, borderRadius: 8, overflow: "hidden", opacity: disabled ? 0.6 : 1 }}>
                        <ZenToolbar
                            editor={editorRef}
                            actions={toolbarActions}
                            disabled={disabled}
                            iconTint={theme?.lightDark}
                            selectedIconTint={theme?.primary}
                            disabledIconTint={theme?.lightGrey}
                            style={{ backgroundColor: theme?.lightBackgroundColor, borderBottomWidth: 1, borderBottomColor: theme?.borderColor }}
                        />
                        <ZenEditor
                            ref={editorRef}
                            initialContentHTML={toEditorHtml(value)}
                            onChange={(html) => onChange(isEditorHtmlEmpty(html) ? "" : html)}
                            placeholder={placeholder}
                            disabled={disabled}
                            initialHeight={height}
                            useContainer
                            editorStyle={{
                                backgroundColor: theme?.light,
                                color: theme?.dark,
                                caretColor: theme?.primary,
                                placeholderColor: theme?.labelColor,
                                contentCSSText: "font-size: 14px; padding: 8px 10px;"
                            }}
                        />
                    </View>
                </View>
            )}
        </ThemeConsumer>
    )
}

export default CommonTextEditor
